import { buildHyperlinkHref } from './bq-hyperlink-columns';
import { resolveSubmissionDisplayIdForResultRow } from './submission-id-display';

/** Persisted assistant metadata when ATS submission completes (`metadata.type`). */
export const SUBMISSION_RESULT_META_TYPE = 'submission_result';

/** Persisted assistant metadata when ATS pipeline (action_type -2) completes (`metadata.type`). */
export const PIPELINE_RESULT_META_TYPE = 'pipeline_result';

export const SUBMISSION_AGENT_TOOL_NAME = 'submission_agent';

export const PIPELINE_AGENT_TOOL_NAME = 'pipeline_agent';

export const TAG_AGENT_TOOL_NAME = 'tag_agent';

/** True for candidate-matching tool nodes (submission, pipeline/tag). */
export function isCandidateMatchingToolName(toolName: string | null | undefined): boolean {
  const t = (toolName ?? '').toString().trim().toLowerCase();
  return (
    t === SUBMISSION_AGENT_TOOL_NAME ||
    t === PIPELINE_AGENT_TOOL_NAME ||
    t === TAG_AGENT_TOOL_NAME
  );
}

export interface SubmissionResultRowPreview {
  applicantName: string;
  email: string;
  jobCode: string;
  jobTitle: string;
  submissionId: string;
  applicantEncryptedId: string;
  jobEncryptedId: string;
  submissionEncryptedId: string;
  submissionHref: string | null;
  jobHref: string | null;
  applicantHref: string | null;
}

export interface SubmissionResultPreviewModel {
  /** Whether this preview is a submission or a pipeline (tag) result. */
  kind: 'submission' | 'pipeline';
  /** Scenario headline shown in the thread bubble. */
  message: string;
  /** Count line shown under the message with View details. */
  summary: string;
  /** @deprecated Use {@link message}. Kept for persisted metadata compatibility. */
  responseMessage: string;
  newSubmissions: SubmissionResultRowPreview[];
  existingSubmissions: SubmissionResultRowPreview[];
}

export interface SubmissionResultDisplayCopy {
  message: string;
  summary: string;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readSubmissionRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is Record<string, unknown> =>
      item != null && typeof item === 'object' && !Array.isArray(item),
  );
}

function firstNonEmpty(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
    if (typeof v === 'number' && Number.isFinite(v)) {
      return String(Math.trunc(v));
    }
  }
  return '';
}

function buildRowPreview(
  row: Record<string, unknown>,
  atsThDomain: string | null | undefined,
): SubmissionResultRowPreview {
  const applicantName =
    asTrimmedString(row['applicant_name']) ||
    asTrimmedString(row['candidate_name']);
  const email = asTrimmedString(row['email']);
  const jobCode = asTrimmedString(row['job_code']);
  const jobTitle = asTrimmedString(row['job_title']);
  // Display code only (`jsaj.submission_id`). Deep link uses encrypted PK separately.
  const submissionId = resolveSubmissionDisplayIdForResultRow(row);
  const submissionEnc = firstNonEmpty(
    row['submission_encrypted_id'],
    row['submissionEncryptedID'],
    row['submissionEncryptedId'],
  );
  const jobEnc = firstNonEmpty(
    row['job_encrypted_id'],
    row['jobEncryptedID'],
    row['jobEncryptedId'],
  );
  const applicantEnc = firstNonEmpty(
    row['applicant_encrypted_id'],
    row['applicantEncryptedID'],
    row['applicantEncryptedId'],
  );
  return {
    applicantName,
    email,
    jobCode,
    jobTitle,
    submissionId,
    applicantEncryptedId: applicantEnc,
    jobEncryptedId: jobEnc,
    submissionEncryptedId: submissionEnc,
    submissionHref: submissionEnc
      ? buildHyperlinkHref(atsThDomain, 'submissions', submissionEnc)
      : null,
    jobHref: jobEnc ? buildHyperlinkHref(atsThDomain, 'jobs', jobEnc) : null,
    applicantHref: applicantEnc
      ? buildHyperlinkHref(atsThDomain, 'applicants', applicantEnc)
      : null,
  };
}

/**
 * Same copy rules as domain-orch `buildSubmissionBatchCopy` (submission) and
 * `buildPipelineBatchCopy` (pipeline → "Pipeline"/"Pipelines").
 * @param jobCodes Optional comma-separated job codes for submission success copy.
 */
export function buildSubmissionBatchCopyFromCounts(
  newCount: number,
  existingCount: number,
  kind: 'submission' | 'pipeline' = 'submission',
  jobCodes = '',
): SubmissionResultDisplayCopy {
  const created = Math.max(0, Math.floor(newCount));
  const skipped = Math.max(0, Math.floor(existingCount));
  const total = created + skipped;
  const isPipeline = kind === 'pipeline';
  const nounSingular = isPipeline ? 'Pipeline' : 'submission';
  const nounPlural = isPipeline ? 'Pipelines' : 'submissions';
  const totalLabel = total === 1 ? nounSingular : nounPlural;
  const summary = `Processed ${total} ${totalLabel}: ${created} created, ${skipped} skipped as duplicates.`;

  if (created > 0 && skipped === 0) {
    const codes = jobCodes.trim() || 'N/A';
    return {
      message: isPipeline
        ? `The selected candidates have been successfully added to the pipeline for Job ${codes}.`
        : `The selected candidates have been successfully submitted for Job ${codes}.`,
      summary,
    };
  }
  if (created === 0 && skipped > 0) {
    const codes = jobCodes.trim() || 'N/A';
    return {
      message: isPipeline
        ? `The selected candidates have already been added to the pipeline for Job ${codes}.`
        : `The selected candidates have already been submitted for Job ${codes}.`,
      summary,
    };
  }
  return {
    message: isPipeline
      ? 'Pipelines processed with duplicates skipped.'
      : 'Submissions processed with duplicates skipped.',
    summary,
  };
}

function uniqueJobCodesFromPreviewRows(rows: SubmissionResultRowPreview[]): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const row of rows) {
    const code = (row.jobCode ?? '').trim();
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);
    const title = (row.jobTitle ?? '').trim();
    labels.push(title ? `${code} · ${title}` : code);
  }
  return labels.join(', ');
}

function uniqueJobCodesFromRawRows(rows: Record<string, unknown>[]): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const row of rows) {
    const code = asTrimmedString(row['job_code']);
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);
    const title = asTrimmedString(row['job_title']);
    labels.push(title ? `${code} · ${title}` : code);
  }
  return labels.join(', ');
}

/** Unique job labels from raw submission result rows (`code · title` when title exists). */
export function formatUniqueJobCodesFromSubmissionRows(
  rows: Record<string, unknown>[],
): string {
  return uniqueJobCodesFromRawRows(rows);
}

function metadataCandidateMatchingKind(
  metadata: Record<string, unknown>,
): 'submission' | 'pipeline' {
  const type = asTrimmedString(metadata['type']);
  const resultKind = asTrimmedString(metadata['resultKind']);
  const isPipeline =
    type === PIPELINE_RESULT_META_TYPE ||
    type === 'pipeline_action' ||
    resultKind === PIPELINE_RESULT_META_TYPE ||
    resultKind === 'pipeline_action';
  return isPipeline ? 'pipeline' : 'submission';
}

function resolveSubmissionCopy(
  metadata: Record<string, unknown>,
  newCount: number,
  existingCount: number,
  jobCodes = '',
): SubmissionResultDisplayCopy {
  const kind = metadataCandidateMatchingKind(metadata);
  const fallback = buildSubmissionBatchCopyFromCounts(
    newCount,
    existingCount,
    kind,
    jobCodes,
  );
  const message =
    asTrimmedString(metadata['message']) ||
    asTrimmedString(metadata['responseMessage']) ||
    asTrimmedString(metadata['ats_message']);
  const summary = asTrimmedString(metadata['summary']);

  // Prefer dynamic job-code copy over stale batch default metadata messages.
  if (isStaleSubmissionBatchDefaultMessage(message, kind, newCount, existingCount)) {
    return {
      message: fallback.message,
      summary: sanitizePipelineTagWording(summary || fallback.summary),
    };
  }

  if (message && summary) {
    return {
      message: kind === 'pipeline' ? sanitizePipelineTagWording(message) : message,
      summary: kind === 'pipeline' ? sanitizePipelineTagWording(summary) : summary,
    };
  }
  return {
    message:
      kind === 'pipeline'
        ? sanitizePipelineTagWording(message || fallback.message)
        : message || fallback.message,
    summary:
      kind === 'pipeline'
        ? sanitizePipelineTagWording(summary || fallback.summary)
        : summary || fallback.summary,
  };
}

/**
 * Rewrite legacy ATS "Tag"/"Tags" recruiter copy to Pipeline/Pipelines.
 * Domain Orch historically said "Tag"; UI and new Domain copy must never show that word.
 */
export function sanitizePipelineTagWording(text: string): string {
  if (!text) {
    return text;
  }
  return text
    .replace(/\bAll Tags created successfully\.?/gi, 'All Pipelines created successfully.')
    .replace(/\bAll Tags already exists\.?/gi, 'All Pipelines already exist.')
    .replace(/\bTags processed with duplicates skipped\.?/gi, 'Pipelines processed with duplicates skipped.')
    .replace(/\bProcessed (\d+) Tags?:/gi, (_m, n: string) => {
      const count = Number(n);
      return `Processed ${n} ${count === 1 ? 'Pipeline' : 'Pipelines'}:`;
    });
}

/** True when metadata still carries the old static batch headline we now replace with job-code copy. */
export function isStaleSubmissionBatchDefaultMessage(
  message: string,
  kind: 'submission' | 'pipeline',
  newCount: number,
  existingCount: number,
): boolean {
  if (kind === 'pipeline') {
    // Always rewrite legacy Tag headlines (any counts).
    if (
      /^All Tags created successfully\.?$/i.test(message) ||
      /^All Tags already exists\.?$/i.test(message) ||
      /^Tags processed with duplicates skipped\.?$/i.test(message)
    ) {
      return true;
    }
  }
  if (newCount > 0 && existingCount === 0) {
    if (kind === 'pipeline') {
      return (
        !message ||
        /^All Pipelines created successfully\.?$/i.test(message) ||
        /^All Tags created successfully\.?$/i.test(message)
      );
    }
    return !message || /^All submissions created successfully\.?$/i.test(message);
  }
  if (newCount === 0 && existingCount > 0) {
    if (!message) {
      return true;
    }
    if (kind === 'pipeline') {
      return (
        /^All Pipelines already exist\.?$/i.test(message) ||
        /^All Tags already exists\.?$/i.test(message)
      );
    }
    return /^All submissions already exist\.?$/i.test(message);
  }
  return false;
}

function toPreviewModel(
  metadata: Record<string, unknown>,
  newSubmissions: SubmissionResultRowPreview[],
  existingSubmissions: SubmissionResultRowPreview[],
): SubmissionResultPreviewModel {
  const jobCodes = uniqueJobCodesFromPreviewRows([
    ...newSubmissions,
    ...existingSubmissions,
  ]);
  const copy = resolveSubmissionCopy(
    metadata,
    newSubmissions.length,
    existingSubmissions.length,
    jobCodes,
  );
  return {
    kind: metadataCandidateMatchingKind(metadata),
    message: copy.message,
    summary: copy.summary,
    responseMessage: copy.message,
    newSubmissions,
    existingSubmissions,
  };
}

/** Builds right-panel preview from assistant metadata or task `output`. */
export function parseSubmissionResultPreviewFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
  atsThDomain?: string | null,
): SubmissionResultPreviewModel | null {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const type = asTrimmedString(metadata['type']);
  const resultKind = asTrimmedString(metadata['resultKind']);
  const candidateMatchingTypes = new Set([
    SUBMISSION_RESULT_META_TYPE,
    'submission_action',
    PIPELINE_RESULT_META_TYPE,
    'pipeline_action',
  ]);
  const isSubmissionMeta =
    candidateMatchingTypes.has(type) || candidateMatchingTypes.has(resultKind);
  if (!isSubmissionMeta) {
    return null;
  }
  const newSubmissions = readSubmissionRows(metadata['new_submissions']).map((row) =>
    buildRowPreview(row, atsThDomain),
  );
  let existingSubmissions = readSubmissionRows(metadata['existing_submissions']).map((row) =>
    buildRowPreview(row, atsThDomain),
  );
  if (newSubmissions.length === 0 && existingSubmissions.length === 0) {
    const nested = metadata['result'];
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      const nestedNew = readSubmissionRows(n['new_submissions']).map((row) =>
        buildRowPreview(row, atsThDomain),
      );
      const nestedExisting = readSubmissionRows(n['existing_submissions']).map((row) =>
        buildRowPreview(row, atsThDomain),
      );
      if (nestedNew.length > 0 || nestedExisting.length > 0) {
        return toPreviewModel({ ...n, ...metadata }, nestedNew, nestedExisting);
      }
    }
    return null;
  }
  return toPreviewModel(metadata, newSubmissions, existingSubmissions);
}

/** Task-graph node `output` from `submission_agent` → preview metadata envelope. */
export function buildSubmissionResultMetadataFromSubmissionOutput(
  output: unknown,
  options?: { taskId?: string; taskGraphId?: string },
): Record<string, unknown> | null {
  if (output == null || typeof output !== 'object' || Array.isArray(output)) {
    return null;
  }
  const o = output as Record<string, unknown>;
  const outType = asTrimmedString(o['type']);
  if (outType !== 'submission_action' && outType !== 'pipeline_action') {
    return null;
  }
  const preview = parseSubmissionResultPreviewFromMetadata(o, null);
  if (!preview) {
    return null;
  }
  const metaType =
    outType === 'pipeline_action' ? PIPELINE_RESULT_META_TYPE : SUBMISSION_RESULT_META_TYPE;
  const taskId = options?.taskId?.trim() ?? '';
  const taskGraphId = options?.taskGraphId?.trim() ?? '';
  return {
    type: metaType,
    resultKind: metaType,
    message: preview.message,
    summary: preview.summary,
    responseMessage: preview.message,
    ats_message: preview.message,
    new_submissions: o['new_submissions'],
    existing_submissions: o['existing_submissions'],
    ...(taskId ? { taskId } : {}),
    ...(taskGraphId ? { taskGraphId } : {}),
  };
}

export function resolveSubmissionResultDisplay(
  metadata: Record<string, unknown> | null | undefined,
): SubmissionResultDisplayCopy {
  const preview = parseSubmissionResultPreviewFromMetadata(metadata, null);
  if (!preview) {
    return {
      message: 'Submission completed.',
      summary: '',
    };
  }
  return {
    message: preview.message,
    summary: preview.summary,
  };
}

/** @deprecated Use {@link resolveSubmissionResultDisplay}. */
export function submissionResultSummaryFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): string {
  return resolveSubmissionResultDisplay(metadata).message;
}

export function submissionResultHasRows(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return parseSubmissionResultPreviewFromMetadata(metadata) != null;
}

function hasServerProvidedCandidateMatchingSummary(
  raw: Record<string, unknown>,
): boolean {
  const type = asTrimmedString(raw['type']);
  const resultKind = asTrimmedString(raw['resultKind']);
  const isResultType =
    type === SUBMISSION_RESULT_META_TYPE ||
    type === PIPELINE_RESULT_META_TYPE ||
    resultKind === SUBMISSION_RESULT_META_TYPE ||
    resultKind === PIPELINE_RESULT_META_TYPE;
  if (!isResultType) {
    return false;
  }
  return (
    asTrimmedString(raw['message']).length > 0 && asTrimmedString(raw['summary']).length > 0
  );
}

/**
 * Canonical assistant metadata for submission / tag (pipeline) results.
 * Prefers server-generated `message` + `summary`; UI fallback copy only for legacy rows.
 */
export function normalizeSubmissionResultAssistantMetadata(
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  if (hasServerProvidedCandidateMatchingSummary(raw)) {
    const metaType =
      asTrimmedString(raw['type']) === PIPELINE_RESULT_META_TYPE ||
      asTrimmedString(raw['resultKind']) === PIPELINE_RESULT_META_TYPE
        ? PIPELINE_RESULT_META_TYPE
        : SUBMISSION_RESULT_META_TYPE;
    const message = asTrimmedString(raw['message']);
    const summary = asTrimmedString(raw['summary']);
    const nested = raw['result'];
    const rowSource =
      nested != null && typeof nested === 'object' && !Array.isArray(nested)
        ? (nested as Record<string, unknown>)
        : raw;
    return {
      ...raw,
      type: metaType,
      resultKind: metaType,
      message,
      summary,
      responseMessage: asTrimmedString(raw['responseMessage']) || message,
      ats_message: asTrimmedString(raw['ats_message']) || message,
      new_submissions: rowSource['new_submissions'] ?? raw['new_submissions'],
      existing_submissions: rowSource['existing_submissions'] ?? raw['existing_submissions'],
    };
  }
  const preview = parseSubmissionResultPreviewFromMetadata(raw, null);
  if (!preview) {
    return null;
  }
  const kind = metadataCandidateMatchingKind(raw);
  const metaType = kind === 'pipeline' ? PIPELINE_RESULT_META_TYPE : SUBMISSION_RESULT_META_TYPE;
  const nested = raw['result'];
  const rowSource =
    nested != null && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : raw;
  return {
    ...raw,
    type: metaType,
    resultKind: metaType,
    message: preview.message,
    summary: preview.summary,
    responseMessage: preview.message,
    ats_message: preview.message,
    new_submissions: rowSource['new_submissions'] ?? raw['new_submissions'],
    existing_submissions: rowSource['existing_submissions'] ?? raw['existing_submissions'],
  };
}
